import React, { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * The one screen seen before the shell exists, so it carries the brand lockup
 * on its own — the mark over the wordmark, in the sidebar's two-tone.
 *
 * It used to wear things nothing else in the app has: two pulsing blurred
 * gradient blobs, a glow shadow under the button, and bold sentence-case
 * labels. Those were the whole reason it read as a different product from the
 * screen it hands you to. What it has now is the ground, a ruled card and the
 * label band — the same parts as every other form here.
 */
const LoginPage: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const { login, isLoading, error } = useAuthStore();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const success = await login(email, password);
        if (success) {
            navigate('/', { replace: true });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4 font-sans">
            <div className="w-full max-w-[26rem]">
                <Card className="p-7 sm:p-9">
                    {/* Brand — stacked, so the wordmark never wraps around the mark */}
                    <div className="flex flex-col items-center text-center">
                        <img src="/logo-mark.png" alt="" className="h-11 w-auto invert dark:invert-0" />
                        <h1 className="mt-5 whitespace-nowrap text-title font-bold tracking-tighter">
                            <span className="text-muted-foreground">Kavas</span>{' '}
                            <span className="text-foreground">Capital-Nightingale</span>
                        </h1>
                        <p className="mt-2 text-micro font-semibold uppercase tracking-eyebrow text-muted-foreground">
                            Proprietary Trading Platform
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-5 border-t border-border pt-7">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address</Label>
                            <div className="relative">
                                <Mail
                                    size={15}
                                    className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground"
                                />
                                <Input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="h-10 pl-9"
                                    placeholder="name@kavas.com"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Account Password</Label>
                            <div className="relative">
                                <Lock
                                    size={15}
                                    className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground"
                                />
                                <Input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="h-10 pl-9"
                                    placeholder="••••••••"
                                    required
                                />
                            </div>
                        </div>

                        {error && (
                            <Alert variant="destructive" className="animate-in fade-in slide-in-from-top-2">
                                <AlertCircle />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        <Button type="submit" disabled={isLoading} className="mt-2 h-10 w-full">
                            {isLoading ? <Loader2 size={15} className="animate-spin" /> : <span>Sign In</span>}
                        </Button>
                    </form>

                    <p className="mt-7 border-t border-border pt-5 text-center text-meta text-muted-foreground">
                        System monitoring active. Unauthorized access attempts are logged.
                    </p>
                </Card>
            </div>
        </div>
    );
};

export default LoginPage;
